import React from 'react';
import { Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence, domAnimation, LazyMotion } from 'framer-motion';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import ScrollToTop from 'utils/scrolltotop.js';
import './assets/styles/App.scss';
import './assets/styles/text.scss';

import OldNavbar from './components/Navbar/OldNavbar';

import Home from './components/Home/Home';

import Team from './components/Team/Team';
import Launcher from './components/Launcher/Launcher';
import Media from './components/Media/Media';
import Roadmap from './components/Roadmap/Roadmap';

import Comic from './components/Comic/Comic';
import SingleComic from './components/Comic/SingleComic';

import News from './components/News/News';
import SingleNews from './components/News/SingleNews';

import Lore from './components/Lore/Lore';
import SingleLore from './components/Lore/SingleLore';

import Faq from './components/Faq/Faq';
import Provenance from './components/Provenance/Provenance';
import Leaderboards from './components/Leaderboard/Leaderboards';
import Leaderboard from './components/Leaderboard/Leaderboard';
import Help from './components/Help/Help';
import Game from './components/Game/Game';
import Terms from './components/Terms/Terms';

import DuskAccount from 'components/Account/DuskAccount';
import Gallery from './components/Gallery/Gallery';
import Login from './components/Login/Login';
import MetamaskLogin from './components/Login/MetamaskLogin';
import DusktechLogin from './components/Login/DusktechLogin';
import Register from './components/Register/Register';

import Error from './components/Error/Error';
import global from './contexts/Global';

import Dialog from './components/Dialog/Dialog';
import InteractiveStories from './components/InteractiveStories';

import Adrian from './components/Adrian/Adrian';
import Mint from './components/Mint/Mint';

import { useBreakerData } from './hooks/useBreakerData';

import { isFeatureEnabled } from 'utils/features';
import { EggLanding } from './components/Egg/EggLanding';
import { EggDashboard } from './components/Egg/EggDashboard';
import { EggStories } from 'components/Egg/EggStories';
import ForgotPassword from 'components/ResetPassword/ForgotPassword';
import { EggMissions } from 'components/Egg/EggMissions';
import { PlayToMint } from './components/PlayToMint/PlayToMint';
import { BreakerCollection } from 'components/Account/BreakerCollection';
import { EggCollection } from 'components/Account/EggCollection';
import { Referral } from 'components/Egg/Referral';
import NFTTerms from 'components/NFTTerms/NFTTerms';
import ResetPassword from 'components/ResetPassword/ResetPassword';
import { GlobalContext } from './contexts';
import { routes } from './routing/routes';
import { FLAGS } from 'hooks/useFlags';
import { ContestLeaderboardRoute } from './components/Leaderboard/ContestLeaderboardPage';
import { BadgeCollection } from 'components/Account/BadgeCollection';
import { BreakerGallery } from 'components/Gallery/BreakerGallery';
import { Navbar } from './components/Navbar/Navbar';
import { OldGallery } from 'components/Gallery/OldGallery';
import GribbleCollection from 'components/Account/GribbleCollection';
import GalleryView from 'components/Gallery/GalleryView';
import { FeatureFlaggedRoute } from './components/FeatureFlagged/FeatureFlagged';
import { CollectionRoute } from 'components/Collection/CollectionView';
import { Play } from 'components/Mint/Play';
import { AdrianSanctuary } from 'components/AdrianSanctuary/AdrianSanctuary';
import { Hatchening } from 'components/Hatchening/Hatchening';
import { OmnigrowUpdateModal } from 'components/Omnigrow/OmnigrowUpdateModal';

function App() {
  global.breakerData = useBreakerData();
  const location = useLocation();

  return (
    <div className="App">
      <GlobalContext>
        <HelmetProvider>
          <Helmet>
            <meta charSet="utf-8" />
            <title>DuskBreakers - An NFT Saga</title>
            <meta
              name="description"
              content="DuskBreakers is an interactive NFT world-building experience. The choices we make collectively will irrevocably change the course of history across an exciting new alternate universe."
            />
          </Helmet>
          <FeatureFlaggedRoute flag={FLAGS.siteHierarchy} else={<OldNavbar />}>
            <Navbar />
          </FeatureFlaggedRoute>
          <LazyMotion features={domAnimation}>
            <AnimatePresence exitBeforeEnter initial={false}>
              <div>
                <ScrollToTop />
                <Routes location={location} key={location.pathname}>
                  <Route path={routes.home()} element={<Home />} exact />
                  <Route path={routes.roadmap()} element={<Roadmap />} />
                  <Route path={routes.team()} element={<Team />} />
                  <Route path={routes.comic.base()} element={<Comic />} />
                  <Route
                    path={routes.comic.single({ id: ':id', slug: ':slug' })}
                    element={<SingleComic />}
                  />
                  <Route path={routes.news.base()} element={<News />} />
                  <Route
                    path={routes.news.single({ id: ':id', slug: ':slug' })}
                    element={<SingleNews />}
                  />
                  <Route path={routes.lore.base()} element={<Lore />} />
                  <Route
                    path={routes.lore.single({ id: ':id', slug: ':slug' })}
                    element={<SingleLore />}
                  />
                  <Route
                    path={routes.adrianSanctuary()}
                    element={
                      <FeatureFlaggedRoute flag={FLAGS.hatchening}>
                        <AdrianSanctuary />
                      </FeatureFlaggedRoute>
                    }
                  />
                  <Route path={routes.hatchening()} element={<Hatchening />} />

                  <Route
                    path={routes.gallery.base()}
                    element={
                      <FeatureFlaggedRoute flag={FLAGS.newGallery} else={<OldGallery />}>
                        <Gallery />
                      </FeatureFlaggedRoute>
                    }
                  />
                  <Route
                    path={routes.gallery.duskbreakers()}
                    element={
                      <FeatureFlaggedRoute flag={FLAGS.newGallery}>
                        <GalleryView />
                      </FeatureFlaggedRoute>
                    }
                  >
                    <Route path="" element={<BreakerGallery />} />
                  </Route>
                  <Route path={routes.faq()} element={<Faq />} />
                  <Route path={routes.terms()} element={<Terms />} />
                  <Route path={routes.adrian()} element={<Adrian />} />
                  <Route path={routes.launcher()} element={<Launcher />} />
                  <Route path={routes.media()} element={<Media />} />
                  <Route path={routes.provenance()} element={<Provenance />} />
                  <Route path={routes.leaderboards.base()} element={<Leaderboards />} />
                  <Route
                    path={routes.leaderboards.singleLeaderboard({ type: ':type' })}
                    element={<Leaderboard />}
                  />
                  <Route path={routes.help()} element={<Help />} />
                  <Route path={routes.game()} element={<Game />} />
                  <Route path={routes.interactiveStories.base()} element={<InteractiveStories />} />
                  <Route
                    path={routes.interactiveStories.dialog({ slug: ':slug' })}
                    element={<Dialog />}
                  />
                  <Route path={routes.nftTerms()} element={<NFTTerms />} />
                  <Route path={routes.duskAccount.base()} element={<DuskAccount />}>
                    <Route path="" element={<BreakerCollection />} />
                    <Route path={routes.duskAccount.eggCollection()} element={<EggCollection />} />

                    <Route
                      path={routes.duskAccount.badgeCollection()}
                      element={<BadgeCollection />}
                    />

                    <Route
                      path={routes.duskAccount.gribbleCollection()}
                      element={
                        <FeatureFlaggedRoute
                          flag={(isFlagEnabled) => isFlagEnabled(FLAGS.hatchening)}
                        >
                          <GribbleCollection />
                        </FeatureFlaggedRoute>
                      }
                    />
                  </Route>
                  <Route path={routes.login()} element={<Login />}>
                    <Route path={routes.loginDusktech()} element={<DusktechLogin />} />
                    <Route path={routes.loginMetamask()} element={<MetamaskLogin />} />
                  </Route>
                  {isFeatureEnabled('TEST_ROUTES') && (
                    <Route path="/lore-test" element={<SingleLore shouldRenderTestPost={true} />} />
                  )}

                  <Route
                    path={routes.play()}
                    element={
                      <FeatureFlaggedRoute flag={FLAGS.hatchening} else={<EggLanding />}>
                        <Play />
                      </FeatureFlaggedRoute>
                    }
                  />
                  <Route
                    path={routes.egg.play()}
                    element={<Navigate replace to={routes.play()} />}
                  />

                  <Route path={routes.mint()} element={<Mint />} />
                  <Route
                    path={routes.egg.mint()}
                    element={<Navigate replace to={routes.mint()} />}
                  />

                  <Route
                    path={routes.dashboard()}
                    element={
                      <FeatureFlaggedRoute
                        flag={(isFlagEnabled) => !isFlagEnabled(FLAGS.hatchening)}
                      >
                        <EggDashboard />
                      </FeatureFlaggedRoute>
                    }
                  />

                  <Route
                    path={routes.stories()}
                    element={
                      <FeatureFlaggedRoute
                        flag={(isFlagEnabled) => !isFlagEnabled(FLAGS.hatchening)}
                      >
                        <EggStories />
                      </FeatureFlaggedRoute>
                    }
                  />
                  <Route
                    path={routes.missions()}
                    element={
                      <FeatureFlaggedRoute
                        flag={(isFlagEnabled) => !isFlagEnabled(FLAGS.hatchening)}
                      >
                        <EggMissions />
                      </FeatureFlaggedRoute>
                    }
                  />

                  <Route
                    path={routes.leaderboard.page({ type: ':type' })}
                    element={
                      <FeatureFlaggedRoute
                        flag={(isFlagEnabled) => !isFlagEnabled(FLAGS.hatchening)}
                      >
                        <ContestLeaderboardRoute />
                      </FeatureFlaggedRoute>
                    }
                  />
                  <Route
                    path={routes.leaderboard.base()}
                    element={
                      <FeatureFlaggedRoute
                        flag={(isFlagEnabled) => !isFlagEnabled(FLAGS.hatchening)}
                      >
                        <ContestLeaderboardRoute />
                      </FeatureFlaggedRoute>
                    }
                  />

                  <Route
                    path={routes.egg.leaderboard.base()}
                    element={<Navigate replace to={routes.leaderboard.base()} />}
                  />
                  <Route
                    path={routes.egg.leaderboard.page({ type: ':type' })}
                    element={<Navigate replace to={routes.leaderboard.page({ type: ':type' })} />}
                  />

                  <Route path={routes.referral()} element={<Referral />} />

                  <Route
                    path={routes.collection.page({ filter: ':filter' })}
                    element={
                      <FeatureFlaggedRoute flag={FLAGS.hatchening}>
                        <CollectionRoute />
                      </FeatureFlaggedRoute>
                    }
                  />

                  <Route
                    path={routes.collection.base()}
                    element={
                      <FeatureFlaggedRoute flag={FLAGS.hatchening}>
                        <CollectionRoute />
                      </FeatureFlaggedRoute>
                    }
                  />

                  <Route path="/account" element={<Navigate replace to={routes.register()} />} />
                  <Route path={routes.register()} element={<Register />} />
                  <Route
                    path={`${routes.playToMint()}`}
                    element={
                      <PlayToMint callToAction={{ text: 'Login', href: routes.loginDusktech() }} />
                    }
                  />
                  <Route
                    path="/ref/:referralCode"
                    element={
                      <PlayToMint
                        callToAction={{ text: 'Create New Account', href: routes.login() }}
                      />
                    }
                  />
                  <Route path={routes.forgotPassword()} element={<ForgotPassword />} />
                  <Route path={routes.resetPassword()} element={<ResetPassword />} />
                  <Route path={routes['404']()} element={<Error />} />
                  <Route path="*" element={<Navigate replace to={routes['404']()} />} />
                </Routes>
                <OmnigrowUpdateModal />
              </div>
            </AnimatePresence>
          </LazyMotion>
        </HelmetProvider>
      </GlobalContext>
    </div>
  );
}

export default App;
